import productsServiceCommon from '../';
import constants from '../../../settings/constants';
import { articleNo } from '../../../util/articleNo';
import { isStandAlone } from '../models';
import { FILTERS, ITEMS, MISSING_PRODUCT_CATEGORIES } from '../../../constants';
import { colorFilterObject, subFilterObject } from '../itemsFilters';

const articleImages = [];
const sectionImages = [];

/**
 * See {@link getFilters} for docs on filters structure.
 * @type {[{filter: (function(*=): function({product: *}): boolean), condition: (function(): boolean), Component: () => JSX.Element}]}
 */
const getFilters = () => [
  colorFilterObject(),
  subFilterObject({ appliesTo: [FILTERS.SECTIONS] }),
];

function makeSectionImages() {
  const imagelist = productsServiceCommon
    .getAll(true)
    .filter(productsServiceCommon.isSection)
    .map(function (section) {
      const [width, _, height] = section.modelid.match(/\d+/g); // eslint-disable-line no-unused-vars
      const { color } = section.filter;

      const sceneFileName = `BROR_post_${height}_${width}_${color}.png`;
      return {
        name: section.id,
        url: `${constants.IMAGE_ROOT}${sceneFileName}`,
        thumbnailUrl: `${constants.IMAGE_ROOT}thumbnails/${sceneFileName}`,
        thumbnailUrlPortrait: `${constants.IMAGE_ROOT}thumbnails/portrait/${sceneFileName}`,
        thumbnailUrlRTL: `${constants.IMAGE_ROOT}thumbnails/rtl/${sceneFileName}`,
      };
    });

  sectionImages.push(...imagelist);
}

function isInsert(id) {
  const found = productsServiceCommon.getProduct(id, true);
  return found && !isStandAlone(found);
}

function makeArticleImages() {
  const imagelist = productsServiceCommon
    .getAll()
    .filter(item => item.modelid)
    .filter(
      item =>
        !productsServiceCommon.isSection(item) &&
        !productsServiceCommon.isType(item, 'shelves')
    )
    .map(product => {
      const { color } = product.filter;
      let articleId = product.id;

      if (!articleNo(articleId)) {
        const includedArticles = productsServiceCommon.getIncludedArticles(
          product.id
        );

        while (includedArticles.length && !articleNo(articleId)) {
          articleId = includedArticles.shift();
        }
      }

      const sceneFileName = `${articleNo(articleId)}_${
        product.modelid
      }_${color}_0001.png`;
      const menuFileName = `${articleNo(articleId)}_${
        product.modelid
      }_${color}_t_0001.png`;

      const hasPortraitSpecificThumb = productsServiceCommon.isType(product, [
        ITEMS.CABINET,
        ITEMS.PEGBOARD,
        ITEMS.TROLLEY,
        ITEMS.WORKBENCH,
      ]);
      return {
        name: product.id,
        url: `${constants.IMAGE_ROOT}${sceneFileName}`,
        thumbnailUrl: `${constants.IMAGE_ROOT}thumbnails/${menuFileName}`,
        thumbnailUrlPortrait: hasPortraitSpecificThumb
          ? `${constants.IMAGE_ROOT}thumbnails/portrait/${menuFileName}`
          : null,
        thumbnailUrlRTL: `${constants.IMAGE_ROOT}thumbnails/rtl/${menuFileName}`,
      };
    });

  articleImages.push(...imagelist);

  makeSectionImages();
}

function getArticleImage(id) {
  return articleImages.concat(sectionImages).find(image => image.name === id);
}

function getDragMode(product) {
  return isInsert(product.id)
    ? constants.DRAG_MODE.INSERT
    : constants.DRAG_MODE.FLOAT;
}

function getFit(item, slot) {
  if (
    productsServiceCommon.isType(item, [
      ITEMS.SHELF,
      ITEMS.CABINET,
      ITEMS.DRAWER,
    ])
  ) {
    const color = slot.color || item.filter.color;

    let out = productsServiceCommon.getFilteredItems(
      _item => productsServiceCommon.isType(_item, [item.filter.type]),
      {
        width: slot.width,
        depth: slot.depth,
        color: color,
      }
    )[0];

    if (!out && productsServiceCommon.isCabinet(item) && color === 'wood') {
      out = productsServiceCommon.getFilteredItems(
        _item => productsServiceCommon.isType(_item, [item.filter.type]),
        {
          width: slot.width,
          depth: slot.depth,
          color: 'black',
        }
      )[0];
    }

    return Object.assign({}, out);
  } else if (productsServiceCommon.isAddOnShelf(item)) {
    const out = productsServiceCommon.getProduct(
      slot.depth <= 400 ? '00382792' : '20402001'
    );
    return Object.assign({}, out);
  }

  return null;
}

function isModule(id) {
  const found = productsServiceCommon.getProduct(id, true);
  return found && isStandAlone(found) && found.filter.type !== ITEMS.POST;
}

function isSection(id) {
  const found = productsServiceCommon.getProduct(id, true);
  return found && found.filter.type === ITEMS.POST;
}

function isMultiParentProduct(item) {
  // Not implemented for Bror
  return false;
}

/**
 * Gets the items relation to its parent section in terms of offsets.
 * This is calculated as (section.width - item.width) / 2 for the x axis.
 * @param {object} item
 * @returns {object} Dimensions of the offset for the 3 axis.
 */
function getSectionOffset(item) {
  if (productsServiceCommon.isType(item, [ITEMS.SHELF, ITEMS.DIVIDER])) {
    return { x: 5, y: 0, z: 5 };
  }
  if (productsServiceCommon.isType(item, ITEMS.CABINET)) {
    return { x: 4, y: 0, z: 0 };
  }
  if (productsServiceCommon.isType(item, ITEMS.DRAWER)) {
    return { x: 4, y: 0, z: -4 };
  }
  return { x: 0, y: 0, z: 0 };
}

function getSectionInserts(...filters) {
  return productsServiceCommon.getFilteredItems(
    item => productsServiceCommon.isType(item, [ITEMS.SHELF]),
    ...filters
  );
}

function shouldHaveColorSelector(item) {
  return productsServiceCommon.isType(item, [
    ITEMS.SHELF,
    ITEMS.PEGBOARD,
    ITEMS.POST,
    ITEMS.CABINET,
    ITEMS.TROLLEY,
    ITEMS.WORKBENCH,
  ]);
}

/**
 * Returns boolean whether or not to show info icon for items
 *
 * @param id
 * @returns {boolean}
 */
const showInfoIcon = id =>
  !productsServiceCommon.isType(id, constants.HIDE_INFO_ICON_FOR_TYPE);

function getMissingMandatoryProductCategories() {
  const sections = productsServiceCommon.getFilteredItems(item =>
    isSection(item)
  );
  return sections.length === 0 ? [MISSING_PRODUCT_CATEGORIES.SECTION] : [];
}

export default {
  articleImages,
  getArticleImage,
  getDragMode,
  getFit,
  isModule,
  isInsert,
  isSection,
  isMultiParentProduct,
  getSectionOffset,
  getSectionInserts,
  makeArticleImages,
  shouldHaveColorSelector,
  showInfoIcon,
  getMissingMandatoryProductCategories,
  getFilters,
};
